/**
 * The grouping-chain editor for the rollup's configurable tab (PlanRollup-View-Shaping §9).
 *
 * The shared chain editor (GroupByChain) with the rollup's levels: the four fixed sources plus one entry per
 * active DimensionDefinition, built at runtime, so a new customer dimension becomes groupable with no code
 * change.
 *
 * The chain is a display concern only: grouping re-partitions and never changes the total (§14), so
 * nothing here can make the figures disagree with the page's KPI tiles. That is the filter's problem,
 * and the filter is a later phase.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';
import { GroupByChain } from '@/components/grouping/GroupByChain';

/** Mirrors RollupGroupBy.MaxLevels — each level multiplies row cardinality. */
export const MAX_GROUP_LEVELS = 4;

const DIMENSION_PREFIX = 'DIMENSION:';

interface Props {
  /** The applied chain as server tokens, outermost first. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Reset target — the tab's preset, so "back to how it was" is one click. */
  defaultValue: string[];
}

export function GroupByChips({ value, onChange, defaultValue }: Props) {
  const { t } = useTranslation();
  const { data: dimensions } = useDimensionDefinitions();

  const activeDimensions = useMemo(() => (dimensions ?? []).filter((d) => d.isActive), [dimensions]);

  const levels = useMemo(
    () => [
      { token: 'ORG_NODE', label: t('Finance.PlanRollup.GroupByOrgNode', { defaultValue: 'Org node' }) },
      { token: 'ACCOUNT_GROUP', label: t('Finance.PlanRollup.GroupByAccountGroup', { defaultValue: 'Account group' }) },
      { token: 'ACCOUNT_TYPE', label: t('Finance.PlanRollup.GroupByAccountType', { defaultValue: 'Account type' }) },
      { token: 'ACCOUNT', label: t('Finance.PlanRollup.GroupByAccount', { defaultValue: 'Account' }) },
      ...activeDimensions.map((d) => ({ token: `${DIMENSION_PREFIX}${d.id}`, label: d.name })),
    ],
    [t, activeDimensions],
  );

  return (
    <GroupByChain
      value={value}
      onChange={onChange}
      defaultValue={defaultValue}
      // A dimension that has since been deactivated is offered no more and its chip shows its token: a blank chip would
      // read as a level with no name rather than one that no longer exists.
      levels={levels}
      flatLabel={t('Finance.PlanRollup.GroupByFlat', { defaultValue: 'No grouping — one row per account' })}
      maxLevels={MAX_GROUP_LEVELS}
    />
  );
}

export default GroupByChips;
